import React, { useContext, useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, Star, Send, MessageSquare, AlertTriangle, Search, ChevronDown } from "lucide-react";
import { net } from "../../../database";
import {
  LanguageContext,
  StaffContext,
  UserContext,
  StudentsContext,
  CustomersContext,
} from "../../../Context";
import HtComplete from "./HtComplete";
import PremiumButton from "../../content/components/PremiumButton";
import Lang from "../../content/components/Lang";
import "./newMessageDialog.css";

/**
 * NewMessageDialog - Compose a new message or complaint
 * Replaces MUI Dialog with custom portal-based dialog.
 *
 * @author opencode + glm-5.1
 * @date 2026-04-21
 * @version 2.0
 *
 * @param {Object} props
 * @param {boolean} props.messageDialogOpen - Controls dialog visibility
 * @param {Function} props.setMessageDialogOpen - Setter for dialog visibility
 * @param {string} [props.description] - Pre-filled description text
 */
export default function NewMessageDialog({
  messageDialogOpen,
  setMessageDialogOpen,
  description: descriptionProp,
}) {
  const { language, l } = useContext(LanguageContext);
  const { user } = useContext(UserContext);
  const { students } = useContext(StudentsContext);
  const { customers } = useContext(CustomersContext);
  const { staff } = useContext(StaffContext);

  const [message, setMessage] = useState({
    type: "Message",
    tags: [],
    importance: 0,
    description: descriptionProp || "",
    who: [],
  });

  const [staffSearch, setStaffSearch] = useState("");
  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [isClosing, setIsClosing] = useState(false);
  const staffSearchRef = useRef(null);
  const staffDropdownRef = useRef(null);

  const activeStaff = staff
    .filter((s) => s.status === 0)
    .map((s) => ({ id: s._id, name: s.name }));

  useEffect(() => {
    setMessage({ type: "Message", tags: [], importance: 0, description: descriptionProp || "", who: [] });
    setIsClosing(false);
  }, [messageDialogOpen, descriptionProp]);

  useEffect(() => {
    if (!staffDropdownOpen) return;
    const handler = (e) => {
      if (staffDropdownRef.current && !staffDropdownRef.current.contains(e.target)) {
        setStaffDropdownOpen(false);
        setStaffSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [staffDropdownOpen]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => setMessageDialogOpen(false), 180);
  }, [setMessageDialogOpen]);

  useEffect(() => {
    if (!messageDialogOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [messageDialogOpen, handleClose]);

  const sendNewMessage = () => {
    const newMessage = {
      ...message,
      recordedBy: user?._id,
    };
    newMessage.who = newMessage.who.map((m) => ({ _id: m.id }));
    net.post("/messages", newMessage).then((result) => {
      if (result.sent) {
        setMessage({ type: "Message", tags: [], importance: 0, description: "", who: [] });
        setMessageDialogOpen(false);
      }
    });
  };

  const toggleStaff = (person) => {
    const exists = message.who.find((w) => w.id === person.id);
    if (exists) {
      setMessage((prev) => ({ ...prev, who: prev.who.filter((w) => w.id !== person.id) }));
    } else {
      setMessage((prev) => ({ ...prev, who: [...prev.who, person] }));
    }
  };

  const removeStaff = (personId) => {
    setMessage((prev) => ({ ...prev, who: prev.who.filter((w) => w.id !== personId) }));
  };

  const filteredStaff = activeStaff.filter(
    (s) =>
      s.name.toLowerCase().includes(staffSearch.toLowerCase()) &&
      !message.who.find((w) => w.id === s.id)
  );

  const getInitials = (name) => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const avatarColors = [
    "bg-teal-600", "bg-cyan-600", "bg-emerald-600",
    "bg-violet-600", "bg-rose-600", "bg-amber-600",
    "bg-sky-600", "bg-indigo-600",
  ];

  const getColorForName = (name) => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };

  if (!students || !customers) return null;
  if (!messageDialogOpen) return null;

  const canSend = message.who && message.who.length > 0;

  return createPortal(
    <div
      className={`nmsg-backdrop ${isClosing ? "nmsg-backdrop--closing" : ""}`}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div className={`nmsg-dialog ${isClosing ? "nmsg-dialog--closing" : ""}`}>
        {/* Header */}
        <header className="nmsg-header">
          <div className="nmsg-header-left">
            <span className="nmsg-header-icon">
              <MessageSquare size={18} />
            </span>
            <h2 className="nmsg-header-title">
              <Lang w="newMessageOrComplaint" />
            </h2>
          </div>
          <button type="button" className="nmsg-close-btn" onClick={handleClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {/* Content */}
        <div className="nmsg-content">
          {/* Type Toggle */}
          <div className="nmsg-section">
            <label className="nmsg-label">
              <Lang w="typeOfMessage" />
            </label>
            <div className="nmsg-toggle">
              <div
                className={`nmsg-toggle-indicator ${
                  message.type === "Complaint" ? "nmsg-toggle-indicator--complaint" : ""
                }`}
              />
              <button
                type="button"
                className={`nmsg-toggle-btn ${
                  message.type === "Message" ? "nmsg-toggle-btn--active" : ""
                }`}
                onClick={() => setMessage((prev) => ({ ...prev, type: "Message" }))}
              >
                <MessageSquare size={14} />
                <Lang w="message" />
              </button>
              <button
                type="button"
                className={`nmsg-toggle-btn ${
                  message.type === "Complaint" ? "nmsg-toggle-btn--active" : ""
                }`}
                onClick={() => setMessage((prev) => ({ ...prev, type: "Complaint" }))}
              >
                <AlertTriangle size={14} />
                <Lang w="complaint" />
              </button>
            </div>
          </div>

          {/* Importance Stars */}
          <div className="nmsg-section">
            <label className="nmsg-label">
              <Lang w="importance" />
            </label>
            <div className="nmsg-stars">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  className={`nmsg-star ${
                    (hoveredStar || message.importance) >= star ? "nmsg-star--filled" : ""
                  }`}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(0)}
                  onClick={() => setMessage((prev) => ({ ...prev, importance: star }))}
                  aria-label={`Rate ${star} out of 5`}
                >
                  <Star size={20} />
                </button>
              ))}
              {(message.importance > 0 || hoveredStar > 0) && (
                <span className="nmsg-stars-label">
                  {["", "Low", "Medium", "High", "Very High", "Urgent"][
                    hoveredStar || message.importance
                  ]}
                </span>
              )}
            </div>
          </div>

          {/* Student Tags */}
          <div className="nmsg-section">
            <label className="nmsg-label">
              <Lang w="tags" />
            </label>
            <HtComplete
              tagged={message.tags}
              setTagged={(tags) => setMessage((prev) => ({ ...prev, tags }))}
            />
          </div>

          {/* Description */}
          <div className="nmsg-section">
            <label className="nmsg-label" htmlFor="nmsg-description">
              <Lang w="description" />
            </label>
            <div className="nmsg-textarea-wrapper">
              <textarea
                id="nmsg-description"
                className="nmsg-textarea"
                rows={4}
                value={message.description}
                onChange={(e) =>
                  setMessage((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder={
                  language === 0
                    ? "Describe the message or complaint..."
                    : "Mô tả tin nhắn hoặc khiếu nại..."
                }
              />
            </div>
          </div>

          {/* Staff Multi-Select */}
          <div className="nmsg-section">
            <label className="nmsg-label">
              <Lang w="staffMembersWhoWillDealWithThisMessageOrComplaint" />
            </label>

            {/* Selected staff chips */}
            {message.who.length > 0 && (
              <div className="nmsg-chips">
                {message.who.map((person) => (
                  <span key={person.id} className="nmsg-chip">
                    <span className={`nmsg-chip-avatar ${getColorForName(person.name)}`}>
                      {getInitials(person.name)}
                    </span>
                    <span className="nmsg-chip-name">{person.name}</span>
                    <button
                      type="button"
                      className="nmsg-chip-remove"
                      onClick={() => removeStaff(person.id)}
                      aria-label={`Remove ${person.name}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Staff search dropdown */}
            <div className="nmsg-staff-select" ref={staffDropdownRef}>
              <div className="nmsg-staff-input-wrapper">
                <Search size={14} className="nmsg-staff-input-icon" />
                <input
                  ref={staffSearchRef}
                  type="text"
                  className="nmsg-staff-input"
                  placeholder={
                    language === 0 ? "Search and add staff..." : "Tìm và thêm nhân viên..."
                  }
                  value={staffSearch}
                  onChange={(e) => {
                    setStaffSearch(e.target.value);
                    setStaffDropdownOpen(true);
                  }}
                  onFocus={() => setStaffDropdownOpen(true)}
                />
                <button
                  type="button"
                  className="nmsg-staff-dropdown-toggle"
                  onClick={() => setStaffDropdownOpen((prev) => !prev)}
                  tabIndex={-1}
                >
                  <ChevronDown size={14} />
                </button>
              </div>

              {staffDropdownOpen && filteredStaff.length > 0 && (
                <div className="nmsg-staff-dropdown">
                  {filteredStaff.slice(0, 20).map((person) => (
                    <button
                      key={person.id}
                      type="button"
                      className="nmsg-staff-option"
                      onClick={() => {
                        toggleStaff(person);
                        setStaffSearch("");
                        staffSearchRef.current?.focus();
                      }}
                    >
                      <span className={`nmsg-staff-option-avatar ${getColorForName(person.name)}`}>
                        {getInitials(person.name)}
                      </span>
                      <span className="nmsg-staff-option-name">{person.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {staffDropdownOpen && filteredStaff.length === 0 && staffSearch && (
                <div className="nmsg-staff-dropdown nmsg-staff-dropdown--empty">
                  {language === 0 ? "No staff found" : "Không tìm thấy nhân viên"}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="nmsg-footer">
          <PremiumButton variant="ghost" size="sm" onClick={handleClose}>
            <Lang w="cancel" />
          </PremiumButton>
          <PremiumButton
            variant="primary"
            size="sm"
            onClick={sendNewMessage}
            disabled={!canSend}
            leftIcon={<Send size={14} />}
          >
            <Lang w="send" />
          </PremiumButton>
        </footer>
      </div>
    </div>,
    document.body
  );
}
